import moment from "moment"
import momenttz from "moment-timezone"

/**
 * Returns a sort function that sorts an array of objects in descending order based on a given field.
 * @param {string|function} field - The field to sort by. Can be a string representing the field name or a function that returns the value to sort by.
 * @param {boolean} [nested=false] - Whether the field is nested within the object.
 * @returns {Function} A sort function that can be used with the Array.sort() method.
 */
export function getBasicSortDesc(field, nested) {
  return (a, b) => {
    const aVal = !a ? a : 'function' == typeof field ? field(a) : nested ? getNestedProperty(field, a) : a[field];
    const bVal = !b ? b : 'function' == typeof field ? field(b) : nested ? getNestedProperty(field, b) : b[field];
    return bVal == null
      ? -1
      : aVal == null
      ? 1
      : bVal > aVal
      ? 1
      : bVal < aVal
      ? -1
      : 0
  }
}

/**
 * Returns a sorting function that sorts an array of objects in ascending order based on the specified field.
 * @param {string|function} field - The field to sort by. Can be a string representing the field name or a function that returns the value to sort by.
 * @param {boolean} [nested=false] - Whether the field is a nested property.
 * @returns {function} A sorting function that can be used with the Array.sort() method.
 */
export function getBasicSortAsc(field, nested) {
  return (b, a) => {
    const aVal = !a ? a : 'function' == typeof field ? field(a) : nested ? getNestedProperty(field, a) : a[field]
    const bVal = !b ? b : 'function' == typeof field ? field(b) : nested ? getNestedProperty(field, b) : b[field]
    return aVal == null
      ? -1
      : bVal == null
      ? 1
      : bVal > aVal
      ? 1
      : bVal < aVal
      ? -1
      : 0
  }
}

export function getRandomItemsUnicos(lista = [], cant) {
  const resultado = [];
  
  // Validar que el segundo argumento sea un número entero positivo
  if (!Number.isInteger(cant) || cant <= 0) {
    throw new Error('La cantidad debe ser un número entero positivo');
  }

  if(cant > lista.length) return lista
  
  // Obtener n índices aleatorios únicos del array
  const indicesAleatoriosUnicos = new Set();
  while (indicesAleatoriosUnicos.size < cant) {
    const indiceAleatorio = Math.floor(Math.random() * lista.length);
    indicesAleatoriosUnicos.add(indiceAleatorio);
  }
  
  // Recuperar los elementos correspondientes a los índices aleatorios del array
  for (const indice of indicesAleatoriosUnicos) {
    resultado.push(lista[indice]);
  }
  
  return resultado;
}

export function getFichaTruperUrl(codigo) {
  return `https://www.truper.com/ficha_tecnica/controllers/index.php?codigo=${codigo}`
};

// https://www.truper.com/CatVigente/liveSearch?codigo=16095
export function getCatalogoTruperUrl(codigo) {
  return `https://www.truper.com/CatVigente/buscador?palabra=${codigo}`
};

// https://www.truper.com/catalogsearch/result/?q=16095
export function getSearchTruperUrl(codigo) {
  return `https://www.truper.com/catalogsearch/result/?q=${codigo}`
};

export function getTruperThumbnailImageByCodigo(codigo) {
  return codigo
    ? `https://www.truper.com/admin/images/ch/${codigo}.jpg`
    : "https://i.postimg.cc/1RDsgtKZ/image.png"
}

export function getTruperImageByClave(clave) {
  return clave
    ? `https://www.truper.com/media/import/imagenes/${clave
        .trim()
        .toUpperCase()
        .split("Ñ")
        .join("N")
        .split("/")
        .join("-")
        .split(" ")
        .join("-")}.jpg`
    : "https://i.postimg.cc/1RDsgtKZ/image.png"
}

export function getNestedProperty(path, obj, separator = ".") {
  if(!path || !obj) return
  const properties = Array.isArray(path) ? path : path.split(separator)
  return properties.reduce((prev, curr) => prev?.[curr], obj)
}

export function delay(t = 1000) {
  return new Promise(resolve => setTimeout(resolve, t))
}

export function fixUrl(url, siteUrl) {
  return (url.indexOf("http") >= 0 ? "" : siteUrl) + url
}

export const removeAttributes = (itemInfo, encabezados) => {
  const obj = { ...itemInfo }
  encabezados.forEach(key => {
    delete obj[key]
  })
  return obj
}

export const getAttributes = (
  itemInfo,
  encabezados,
  nullEmptyString = true,
  includeNulls = true
) => {
  if (!itemInfo) return
  if (!encabezados) return {}

  return encabezados.reduce((obj, key) => {
    if (itemInfo[key] || includeNulls)
      obj[key] = itemInfo[key] ?? (nullEmptyString ? "" : undefined)

    return obj
  }, {})
}

export const getFechaCreacionObj = (fecha_date = new Date(), obj) => {
  const fechaObj = {
    fechaCreacionInt: fecha_date.getTime(),
    fechaCreacion: moment(fecha_date).format(),
    fechaCreacionStr: moment(fecha_date).format("LLLL"),
  }

  if (obj) {
    obj.fechaCreacionInt = fechaObj.fechaCreacionInt
    obj.fechaCreacion = fechaObj.fechaCreacion
    obj.fechaCreacionStr = fechaObj.fechaCreacionStr
  }
  return fechaObj
}

export const getFechaCreacionActualizacionObj = (fecha_date = new Date(), obj) => {
  const fechaObj = {
    fechaCreacionInt: fecha_date.getTime(),
    fechaCreacion: moment(fecha_date).format(),
    fechaCreacionStr: moment(fecha_date).format("LLLL"),

    fechaActualizacionInt: fecha_date.getTime(),
    fechaActualizacion: moment(fecha_date).format(),
    fechaActualizacionStr: moment(fecha_date).format("LLLL"),
  }

  if (obj) {
    obj.fechaCreacionInt = fechaObj.fechaCreacionInt
    obj.fechaCreacion = fechaObj.fechaCreacion
    obj.fechaCreacionStr = fechaObj.fechaCreacionStr
    
    obj.fechaActualizacionInt = fechaObj.fechaActualizacionInt
    obj.fechaActualizacion = fechaObj.fechaActualizacion
    obj.fechaActualizacionStr = fechaObj.fechaActualizacionStr
  }
  return fechaObj
}

export const getFechaActualizacionObj = (fecha_date = new Date(), obj) => {
  const fechaObj = {
    fechaActualizacionInt: fecha_date.getTime(),
    fechaActualizacion: moment(fecha_date).format(),
    fechaActualizacionStr: moment(fecha_date).format("LLLL"),
  }

  if (obj) {
    obj.fechaActualizacionInt = fechaObj.fechaActualizacionInt
    obj.fechaActualizacion = fechaObj.fechaActualizacion
    obj.fechaActualizacionStr = fechaObj.fechaActualizacionStr
  }
  return fechaObj
}

const moment_ = (fecha_date,timezone) => timezone ? momenttz.tz(fecha_date, timezone) : moment(fecha_date)

export const getFechaObj = (field = "fecha", fecha_date = new Date(), obj, options={}) => {

  const { lowerCase = false, includeDate=false, timezone } = options

  const separador = options.separador ? options.separador : ""

  const fieldInt = field + separador + (lowerCase ? "int" : "Int")
  const fieldStr = field + separador + (lowerCase ? "str" : "Str")
  const fieldDate = field + separador + (lowerCase ? "date" : "Date")

  const fechaObj = {
    [fieldInt]: fecha_date.getTime(),
    [field]: moment_(fecha_date, timezone).format(),
    [fieldStr]: moment_(fecha_date, timezone).format("LLLL"),
  }
  if(includeDate) fechaObj[fieldDate] = fecha_date

  if (obj) {
    obj[fieldInt] = fechaObj[fieldInt]
    obj[field] = fechaObj[field]
    obj[fieldStr] = fechaObj[fieldStr]
    if(includeDate) obj[fieldDate] = fecha_date
  }
  return fechaObj
}

export const getAntiguedad = (fecha, opciones = {}) => {
  const { hoy, formatoCorto = false, incluirDuracionDias = false } = opciones

  const fecha_moment = moment(fecha)

  const fecha_hoy = hoy ? moment(hoy) : moment()

  const anios = fecha_hoy.diff(fecha_moment, "years")
  const meses_totales = fecha_hoy.diff(fecha_moment, "months")
  const fecha_mas_anios = moment(fecha).add(anios, "years")
  const meses = fecha_hoy.diff(fecha_mas_anios, "months")
  const fecha_mas_meses = moment(fecha).add(meses_totales, "months")
  const dias = fecha_hoy.diff(fecha_mas_meses, "days")
  const días_totales = fecha_hoy.diff(fecha_moment, "days")

  // return `${anios} años, ${meses_totales>=12?meses:meses_totales} meses, ${dias} días`
  return formatoCorto
    ? `${anios}a  ${meses}m ${dias}d` + (incluirDuracionDias ? ` (${días_totales}d)` : "")
    : `${anios} años, ${meses} meses, ${dias} días` + (incluirDuracionDias ? ` (${días_totales} días)` : "")
}

export const getDescripcion = (descCorta, descLarga, caracteres = 150) => {
  const descStr = descCorta ? descCorta : descLarga ? descLarga : ""
  return (
    descStr.substring(0, caracteres) +
    (descStr.length > caracteres ? " . . ." : "")
  )
}

export const precioMoneda = str => {
  if (!str) return
  return str.toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
  })
}

export const numberFormat = value =>
  value != undefined
    ? new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: "MXN",
      }).format(value)
    : value

export const firstAttributes = (item, cant = 5) => {
  if (!item) return

  return Object.entries(item).reduce((obj, [key, value], idx) => {
    if (idx < cant) obj[key] = value
    return obj
  }, {})
}

export const capitalizeFirstLetter = string => {
  if (!string) return
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export const getAniomesActual = () => moment().format("YYYYMM") * 1

export const getAniomesAjuste = (ajuste = 0, aniomes) => {
  if (ajuste === 0) return getAniomesActual()

  const fechaStr = (aniomes ?? moment().format("YYYYMM")) + "01"
  // const fechaStr = "20230101"
  const fecha = moment(fechaStr, "YYYYMM").toDate()
  fecha.setMonth(fecha.getMonth() + ajuste)
  return moment(fecha).format("YYYYMM") * 1
}

export const getAniomesdiaActual = (fecha_date = new Date()) =>
  moment(fecha_date).format("YYYYMMDD") * 1 

export const getKeyFromEmail = email =>
  !email
    ? undefined
    : email.toLowerCase().split(".").join("_PUNTO_").split("@").join("_AT_")


export const convertValuesToMultilineText = values => {
  return Object.entries(values).reduce((acc, [key, val]) => {
    let str = ""

    if (typeof val === "string" && val) {
      str = `${val}`
    } else if (typeof val === "object") {
      str = `${JSON.stringify(val)}`
    } else if (typeof val === "boolean" && val) {
      str = `${key}`
    } else if (val) {
      str = `${val}`
    }

    return acc + (acc && str ? "\n" : "") + str
  }, "")
}